import type { VoidFunctionComponent } from 'react';

import { Badge, BadgeColors, BadgeShapes } from '../../../../../../../../generic';
import DropdownButton from '../../../../../../../../generic/DropdownButton';
import { Checkbox } from '../../../../../../../../generic/Forms';

import styles from './ProjectsTableHeader.module.less';

type ProjectsTableHeaderDropdownProps = {
  isDisabled?: boolean;
  isSetLatestRevisionDisabled?: boolean;
  onAddSelectedProjects: () => void;
  onRemoveSelectedProjects: () => void;
  onSetRevisionToLatest: () => void;
};
const ProjectsTableHeaderDropdown: VoidFunctionComponent<ProjectsTableHeaderDropdownProps> = ({
  isDisabled = false,
  isSetLatestRevisionDisabled,
  onAddSelectedProjects,
  onRemoveSelectedProjects,
  onSetRevisionToLatest,
}) => (
  <DropdownButton
    id="ProjectsTableHeaderDropdown"
    title="Actions"
    disabled={isDisabled}
    pullRight
  >
    <DropdownButton.Item onSelect={onAddSelectedProjects}>
      Add to release group
    </DropdownButton.Item>
    <DropdownButton.Item onSelect={onRemoveSelectedProjects}>
      Remove from release group
    </DropdownButton.Item>
    <DropdownButton.Item disabled={isSetLatestRevisionDisabled} onSelect={onSetRevisionToLatest}>
      Set revision to latest
    </DropdownButton.Item>
  </DropdownButton>
);

type Props = {
  areProjectsLoading: boolean;
  addedProjectsCount: number;
  totalProjectCount: number;
  areAllProjectsSelected: boolean;
  areSomeProjectsSelected: boolean;
  isSelectAllDisabled: boolean;
  onToggleSelectAllProjects: () => void;
  onAddSelectedProjects: () => void;
  onRemoveSelectedProjects: () => void;
  onSetRevisionToLatest: () => void;
};

/**
 * Renders the header for the projects table, which includes the
 * checkbox to select all projects, the projects count, and a dropdown for bulk actions.
 */
const ProjectsTableHeader: VoidFunctionComponent<Props> = ({
  areProjectsLoading,
  addedProjectsCount,
  totalProjectCount,
  areAllProjectsSelected,
  areSomeProjectsSelected,
  isSelectAllDisabled,
  onToggleSelectAllProjects,
  onAddSelectedProjects,
  onRemoveSelectedProjects,
  onSetRevisionToLatest,
}) => {
  return (
    <div className={styles.headerRow}>
      <div className={styles.left}>
        <Checkbox
          isChecked={!areProjectsLoading && areAllProjectsSelected}
          isIndeterminate={!areAllProjectsSelected && areSomeProjectsSelected}
          onChange={onToggleSelectAllProjects}
          isDisabled={areProjectsLoading || isSelectAllDisabled}
          isRippleDisabled
        />
        <span className={styles.projectCount}>
          <span>Projects</span>
          <Badge color={BadgeColors.LightGray} shape={BadgeShapes.Circle}>
            {areProjectsLoading ? '...' : totalProjectCount.toLocaleString()}
          </Badge>
        </span>
      </div>
      <ProjectsTableHeaderDropdown
        isDisabled={!areSomeProjectsSelected && !areAllProjectsSelected}
        isSetLatestRevisionDisabled={addedProjectsCount === 0}
        onAddSelectedProjects={onAddSelectedProjects}
        onRemoveSelectedProjects={onRemoveSelectedProjects}
        onSetRevisionToLatest={onSetRevisionToLatest}
      />
    </div>
  );
};

export default ProjectsTableHeader;
